import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { portalApi } from '../lib/portalApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Always probe the session on staff/login routes so logged-in users are
    // detected even if the cookie isn't readable yet. On public marketing
    // pages, only probe when the JS-readable ffm_csrf cookie is present — this
    // avoids a 401 on /api/auth/me (and its console error) for visitors.
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const onAuthRoute = /^\/(admin|portal|login)(\/|$)/.test(path);
    const hasSession = /(?:^|;\s*)ffm_csrf=/.test(document.cookie);
    if (!onAuthRoute && !hasSession) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await portalApi.me();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async ({ token, email, code }) => {
    if (token) await portalApi.verifyToken(token);
    else await portalApi.verifyCode(email, code);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await portalApi.logout(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
